import { resolveClubSportDisplay } from '@/features/clubs/lib/resolveClubSportDisplay'
import type { LeaderboardPageData } from '@/features/leaderboard/usePagedLeaderboard'
import { apiRequest } from '@/shared/api/client'

import type {
  AthleteLeaderboardRow,
  AthletesLeaderboardApiEntry,
  AthletesLeaderboardApiPage,
  AthletesLeaderboardScope
} from './types'

export function athleteEntryToRow(
  entry: AthletesLeaderboardApiEntry
): AthleteLeaderboardRow {
  const sport = resolveClubSportDisplay({
    sport: entry.athlete.sport ?? undefined,
    sport_type: entry.athlete.sport_type
  } as Parameters<typeof resolveClubSportDisplay>[0])
  return {
    rank: entry.rank,
    athleteId: entry.athlete.id,
    displayName: entry.athlete.display_name,
    photoUrl: entry.athlete.photo_url ?? undefined,
    country: entry.athlete.country,
    sportName: sport?.name,
    sportIconUrl: sport?.iconUrl,
    fans: entry.fans ?? 0,
    arenaPoints: entry.ap_score ?? 0,
    totalViews: entry.total_views ?? 0,
    isVerified: entry.athlete.is_verified === true
  }
}

export async function fetchAthletesLeaderboardPage(
  scope: AthletesLeaderboardScope,
  page: number,
  pageSize: number,
  /** The page's language: the sport comes localized. */
  lang?: string
): Promise<LeaderboardPageData<AthleteLeaderboardRow>> {
  const response = await apiRequest<Partial<AthletesLeaderboardApiPage>>({
    path: '/api/v2/leaderboards/athletes',
    method: 'GET',
    query: {
      // Several values go comma-separated (the gateway takes both forms).
      ...(scope.sportTypes.length > 0
        ? { sport_types: scope.sportTypes.join(',') }
        : {}),
      ...(scope.countries.length > 0
        ? { countries: scope.countries.join(',') }
        : {}),
      period: scope.period,
      ...(scope.search ? { search: scope.search } : {}),
      page,
      page_size: pageSize
    },
    ...(lang ? { headers: { Lang: lang } } : {}),
    format: 'json'
  })
  return {
    rows: (response.entries ?? []).map(entry => athleteEntryToRow(entry)),
    total: response.total_count ?? 0
  }
}
